// v2/hooks/usePermission.ts — Permission evaluation hook

import { useMemo } from 'react';
import { Action, ApplicationState, ProductType, Role } from '../types/domain';
import { configurationService } from '../../config';
import { isActionAllowed } from '../../engines/permission-engine';

/**
 * Evaluates whether the given action is allowed for the specified role and context.
 * Queries ConfigurationService for the permission matrix and delegates to PermissionEngine.
 *
 * @param role - The user's role
 * @param action - The action to check
 * @param applicationState - Optional application state context
 * @param productType - Optional product type for matrix lookup
 * @returns true if the action is allowed
 */
export function usePermission(
  role: Role,
  action: Action,
  applicationState?: ApplicationState,
  productType?: ProductType,
): boolean {
  return useMemo(() => {
    if (productType) {
      const matrix = configurationService.getPermissionMatrix(productType);
      return isActionAllowed(matrix, role, action, applicationState);
    }
    // No product type — use default permissions (no matrix lookup)
    return isActionAllowed({} as never, role, action, undefined);
  }, [role, action, applicationState, productType]);
}
